/**
 * ClassPerformanceWorkspace – how one or more classes are doing.
 *
 * The teacher dashboard shows it across the teacher's classes; the class page
 * shows it for one. It reads top-down:
 *
 *   What happened?        KPIs, with a change only when a previous period has data
 *   How is it changing?   class average over time
 *   Where is the problem? lessons, with their weakest questions counted
 *   Who needs attention?  every student, including those with no submissions
 *
 * Everything is derived from the results passed in; the caller decides what to
 * load and who may see it.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  bandCounts,
  dailyTrend,
  formatPercent,
  formatPp,
  groupPerformance,
  inRange,
  isDifficult,
  isValid,
  latestPerStudent,
  lessonTitle,
  previousRange,
  questionStats,
  scoreTone,
  studentPerformance,
  summarise,
  type Result,
  withinRange,
  type StudentPerformance as StudentRowData,
} from '../../lib/analytics/metrics';
import type { SessionSummaryRow } from '../../services/performanceService';
import { MetricCard, MetricGrid } from './MetricCard';
import { DistributionChart, Sparkline, TrendChart } from './charts';
import { DataTable, type Column } from './DataTable';
import { DateRangeFilter, makeRange, type RangeValue } from './DateRangeFilter';
import { AnalyticsEmptyState, AnalyticsErrorState, AnalyticsNote, AnalyticsPanel, ChartSkeleton, TableSkeleton } from './AnalyticsPanel';

export interface WorkspaceClass {
  id: string;
  name: string;
}

export interface WorkspaceStudent {
  uid: string;
  name: string;
  email?: string | null;
  classIds: string[];
}

interface StudentRow {
  uid: string;
  name: string;
  email: string | null;
  perf: StudentRowData | null;
}

interface LessonRow {
  key: string;
  chapterId: string;
  topicId: string;
  title: string;
  subject: string | null;
  lastActivity: number | null;
  assigned: number;
  completed: number;
  summary: ReturnType<typeof summarise>;
  difficultQuestions: number;
}

const delta = (current: number | null, previous: number | null | undefined) =>
  current !== null && previous !== null && previous !== undefined ? current - previous : null;

const selectClass = 'h-9 rounded-md border border-input bg-background px-2.5 text-sm text-foreground';

export function ClassPerformanceWorkspace({
  classes,
  students,
  results,
  sessions,
  passThreshold,
  loading,
  error,
  title = 'Class performance',
  schoolId,
}: {
  classes: WorkspaceClass[];
  students: WorkspaceStudent[];
  results: Result[];
  /** Sessions the viewer can list; null to count sessions from results instead. */
  sessions: SessionSummaryRow[] | null;
  passThreshold: number;
  loading: boolean;
  error?: string | null;
  title?: string;
  /** Carried into drill-down links for admins, who have no school of their own. */
  schoolId?: string;
}) {
  const navigate = useNavigate();
  const [classId, setClassId] = useState<string>('all');
  const [subject, setSubject] = useState<string>('all');
  const [range, setRange] = useState<RangeValue>(() => makeRange('year'));

  const classIds = useMemo(() => new Set(classId === 'all' ? classes.map((c) => c.id) : [classId]), [classId, classes]);
  const subjects = useMemo(
    () => [...new Set(results.map((r) => r.subject).filter((s): s is string => !!s))].sort(),
    [results]
  );

  const scoped = useMemo(
    () => results.filter((r) => r.classId !== null && classIds.has(r.classId) && (subject === 'all' || r.subject === subject)),
    [results, classIds, subject]
  );
  const inPeriod = useMemo(() => scoped.filter((r) => inRange(r, range.range)), [scoped, range]);
  const prevRange = useMemo(() => previousRange(range.range), [range]);
  const inPrevious = useMemo(() => (prevRange ? scoped.filter((r) => inRange(r, prevRange)) : []), [scoped, prevRange]);

  const summary = useMemo(() => summarise(inPeriod, passThreshold), [inPeriod, passThreshold]);
  const previous = useMemo(
    () => (prevRange && inPrevious.some(isValid) ? summarise(inPrevious, passThreshold) : null),
    [prevRange, inPrevious, passThreshold]
  );

  const roster = useMemo(() => students.filter((s) => s.classIds.some((id) => classIds.has(id))), [students, classIds]);
  const perStudent = useMemo(() => studentPerformance(inPeriod, passThreshold), [inPeriod, passThreshold]);
  const activeStudents = perStudent.filter((p) => p.average !== null).length;

  // Ended sessions when the viewer can list them; otherwise the distinct
  // sessions that produced results, which misses sessions with no quiz and
  // results recorded before sessions were linked.
  const conducted = useMemo(() => {
    if (!sessions) return new Set(inPeriod.map((r) => r.sessionId).filter(Boolean)).size;
    return sessions.filter(
      (s) =>
        s.status === 'ended' &&
        s.classId !== null &&
        classIds.has(s.classId) &&
        withinRange(s.startedAt, range.range)
    ).length;
  }, [sessions, classIds, range]);

  const studentBands = useMemo(
    () => bandCounts(perStudent.map((p) => p.average).filter((a): a is number => a !== null)),
    [perStudent]
  );
  const trend = useMemo(
    () => dailyTrend(inPeriod).map((p) => ({ x: p.date, value: p.average, note: `${p.count} submission${p.count === 1 ? '' : 's'}` })),
    [inPeriod]
  );

  const studentRows = useMemo<StudentRow[]>(() => {
    const byUid = new Map<string, StudentRow>();
    for (const s of roster) byUid.set(s.uid, { uid: s.uid, name: s.name, email: s.email ?? null, perf: null });
    for (const p of perStudent) {
      const row = byUid.get(p.studentId);
      if (row) row.perf = p;
      else byUid.set(p.studentId, { uid: p.studentId, name: p.studentName || 'Student', email: null, perf: p });
    }
    return [...byUid.values()];
  }, [roster, perStudent]);

  const lessonRows = useMemo<LessonRow[]>(() => {
    return groupPerformance(inPeriod, (r) => r.lessonKey, lessonTitle, { passThreshold, perStudentLatest: true }).map((g) => {
      const list = inPeriod.filter((r) => r.lessonKey === g.key);
      const sample = list[0];
      const lessonClasses = new Set(list.map((r) => r.classId));
      const assigned = roster.filter((s) => s.classIds.some((id) => lessonClasses.has(id))).length;
      const latest = latestPerStudent(list);
      return {
        key: g.key,
        chapterId: sample.chapterId,
        topicId: sample.topicId,
        title: g.label,
        subject: sample.subject,
        lastActivity: g.lastActivity,
        assigned,
        completed: latest.filter(isValid).length,
        summary: g.summary,
        difficultQuestions: questionStats(latest).questions.filter((q) => isDifficult(q)).length,
      };
    });
  }, [inPeriod, roster, passThreshold]);

  const attention = useMemo(
    () =>
      studentRows
        .filter((r) => r.perf && r.perf.average !== null && r.perf.average < 50 && r.perf.submissions >= 2)
        .sort((a, b) => (a.perf!.average ?? 0) - (b.perf!.average ?? 0)),
    [studentRows]
  );
  const noSubmissions = studentRows.filter((r) => !r.perf || r.perf.average === null).length;

  const lessonLink = (l: LessonRow) => {
    const linkClass = classId !== 'all' ? classId : classes.length === 1 ? classes[0].id : null;
    const params = new URLSearchParams();
    if (linkClass) params.set('classId', linkClass);
    if (schoolId) params.set('schoolId', schoolId);
    const qs = params.toString();
    return `/analytics/lessons/${encodeURIComponent(l.chapterId)}/${encodeURIComponent(l.topicId)}${qs ? `?${qs}` : ''}`;
  };

  const studentColumns: Column<StudentRow>[] = [
    {
      key: 'student',
      header: 'Student',
      sortValue: (r) => r.name,
      cell: (r) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{r.name}</p>
          {r.email && <p className="truncate text-xs text-muted-foreground">{r.email}</p>}
        </div>
      ),
    },
    { key: 'lessons', header: 'Lessons', align: 'right', sortValue: (r) => r.perf?.lessonsCompleted ?? 0, cell: (r) => r.perf?.lessonsCompleted ?? 0 },
    {
      key: 'average',
      header: 'Average',
      align: 'right',
      sortValue: (r) => r.perf?.average ?? null,
      cell: (r) => <span className={`font-semibold ${scoreTone(r.perf?.average, passThreshold)}`}>{formatPercent(r.perf?.average)}</span>,
    },
    { key: 'accuracy', header: 'Accuracy', align: 'right', hideOnMobile: true, sortValue: (r) => r.perf?.accuracy ?? null, cell: (r) => formatPercent(r.perf?.accuracy) },
    { key: 'latest', header: 'Latest', align: 'right', sortValue: (r) => r.perf?.latest?.percentage ?? null, cell: (r) => formatPercent(r.perf?.latest?.percentage) },
    {
      key: 'improvement',
      header: 'Change',
      align: 'right',
      sortValue: (r) => r.perf?.improvement ?? null,
      cell: (r) => (
        <span className={r.perf?.improvement == null ? 'text-muted-foreground' : r.perf.improvement > 0 ? 'text-primary' : r.perf.improvement < 0 ? 'text-destructive' : ''}>
          {formatPp(r.perf?.improvement)}
        </span>
      ),
    },
    { key: 'completion', header: 'Completion', align: 'right', hideOnMobile: true, sortValue: (r) => r.perf?.completion ?? null, cell: (r) => formatPercent(r.perf?.completion) },
    { key: 'trend', header: 'Trend', hideOnMobile: true, cell: (r) => <Sparkline values={r.perf?.trend.slice(-8) ?? []} /> },
    {
      key: 'action',
      header: '',
      hideOnMobile: true,
      cell: (r) => (
        <Link to={`/analytics/students/${r.uid}`} onClick={(e) => e.stopPropagation()} className="text-xs font-medium text-primary hover:underline">
          View
        </Link>
      ),
    },
  ];

  const lessonColumns: Column<LessonRow>[] = [
    {
      key: 'lesson',
      header: 'Lesson',
      sortValue: (l) => l.title,
      cell: (l) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{l.title}</p>
          <p className="truncate text-xs text-muted-foreground">
            {[l.subject, l.lastActivity ? new Date(l.lastActivity).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }) : null].filter(Boolean).join(' · ')}
          </p>
        </div>
      ),
    },
    { key: 'completed', header: 'Completed', align: 'right', sortValue: (l) => l.completed, cell: (l) => `${l.completed} / ${Math.max(l.assigned, l.completed)}` },
    {
      key: 'average',
      header: 'Average',
      align: 'right',
      sortValue: (l) => l.summary.average,
      cell: (l) => <span className={`font-semibold ${scoreTone(l.summary.average, passThreshold)}`}>{formatPercent(l.summary.average)}</span>,
    },
    { key: 'median', header: 'Median', align: 'right', hideOnMobile: true, sortValue: (l) => l.summary.median, cell: (l) => formatPercent(l.summary.median) },
    {
      key: 'range',
      header: 'Low – High',
      align: 'right',
      hideOnMobile: true,
      sortValue: (l) => l.summary.lowest?.percentage ?? null,
      cell: (l) => (l.summary.highest ? `${formatPercent(l.summary.lowest?.percentage)} – ${formatPercent(l.summary.highest.percentage)}` : '—'),
    },
    { key: 'accuracy', header: 'Accuracy', align: 'right', hideOnMobile: true, sortValue: (l) => l.summary.accuracy, cell: (l) => formatPercent(l.summary.accuracy) },
    { key: 'completion', header: 'Completion', align: 'right', hideOnMobile: true, sortValue: (l) => l.summary.completion, cell: (l) => formatPercent(l.summary.completion) },
    {
      key: 'difficult',
      header: 'Hard questions',
      align: 'right',
      sortValue: (l) => l.difficultQuestions,
      cell: (l) => (l.difficultQuestions > 0 ? <span className="font-medium text-destructive">{l.difficultQuestions}</span> : <span className="text-muted-foreground">0</span>),
    },
  ];

  if (error) return <AnalyticsErrorState message={error} />;

  const noData = !loading && results.length === 0;

  return (
    <section className="space-y-5" aria-label={title}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-xl font-semibold text-foreground">{title}</h2>
        {!noData && (
          <div className="flex flex-wrap items-center gap-2">
            {classes.length > 1 && (
              <select aria-label="Class" value={classId} onChange={(e) => setClassId(e.target.value)} className={selectClass}>
                <option value="all">All classes</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            )}
            {subjects.length > 1 && (
              <select aria-label="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} className={selectClass}>
                <option value="all">All subjects</option>
                {subjects.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            )}
            <DateRangeFilter value={range} onChange={setRange} />
          </div>
        )}
      </div>

      {noData ? (
        <AnalyticsPanel title="No performance data yet">
          <AnalyticsEmptyState description="Student performance analytics will appear after students complete a lesson quiz." />
        </AnalyticsPanel>
      ) : (
        <>
          <MetricGrid>
            <MetricCard loading={loading} label="Students" value={roster.length} detail={`${activeStudents} with results in period`} />
            <MetricCard
              loading={loading}
              label="Lessons conducted"
              value={conducted}
              detail={sessions ? 'Class sessions ended' : 'Sessions with results'}
              definition={
                sessions
                  ? 'Live class sessions ended in this period, for the selected classes.'
                  : 'Live class sessions in this period that produced at least one quiz result.'
              }
            />
            <MetricCard
              loading={loading}
              label="Average score"
              value={formatPercent(summary.average)}
              detail={`${summary.validSubmissions} submissions`}
              deltaPp={delta(summary.average, previous?.average)}
              definition="Mean percentage across every quiz submission in the period."
            />
            <MetricCard
              loading={loading}
              label="Completion"
              value={formatPercent(summary.completion)}
              detail="Questions answered"
              deltaPp={delta(summary.completion, previous?.completion)}
              definition="Average share of each quiz's questions that students answered."
            />
            <MetricCard
              loading={loading}
              label="Pass rate"
              value={formatPercent(summary.passRate)}
              detail={`At or above ${passThreshold}%`}
              deltaPp={delta(summary.passRate, previous?.passRate)}
            />
            <MetricCard
              loading={loading}
              label="Accuracy"
              value={formatPercent(summary.accuracy)}
              detail="Correct of attempted"
              deltaPp={delta(summary.accuracy, previous?.accuracy)}
            />
          </MetricGrid>

          <div className="grid gap-5 lg:grid-cols-5">
            <AnalyticsPanel className="lg:col-span-3" title="Performance trend" description="Class average by day">
              {loading ? (
                <ChartSkeleton />
              ) : trend.length < 2 ? (
                <AnalyticsEmptyState compact title="Not enough days yet" description="The trend appears once quizzes are taken on two or more days in this period." />
              ) : (
                <TrendChart data={trend} passThreshold={passThreshold} valueLabel="Class average" />
              )}
            </AnalyticsPanel>
            <AnalyticsPanel className="lg:col-span-2" title="Score distribution" description="Students by their average in this period">
              {loading ? (
                <ChartSkeleton />
              ) : activeStudents === 0 ? (
                <AnalyticsEmptyState compact description="No quiz results in this period." />
              ) : (
                <DistributionChart bands={studentBands} />
              )}
            </AnalyticsPanel>
          </div>

          <AnalyticsPanel title="Lessons" description="Each student's latest attempt counts once. Open a lesson to see where students struggled.">
            {loading ? (
              <TableSkeleton />
            ) : (
              <DataTable
                rows={lessonRows}
                columns={lessonColumns}
                rowKey={(l) => l.key}
                initialSort={{ key: 'lesson', direction: 'asc' }}
                searchPlaceholder="Search lessons"
                searchText={(l) => `${l.title} ${l.subject ?? ''}`}
                pageSize={10}
                onRowClick={(l) => navigate(lessonLink(l))}
                empty={<AnalyticsEmptyState compact title="No lessons in this period" description="Try a longer date range." />}
              />
            )}
            <AnalyticsNote>“Hard questions” are answered correctly by fewer than half of at least three students.</AnalyticsNote>
          </AnalyticsPanel>

          {(attention.length > 0 || noSubmissions > 0) && !loading && (
            <AnalyticsPanel title="Needs attention" description="Students averaging under 50% across two or more quizzes in this period">
              {attention.length === 0 ? (
                <p className="text-sm text-muted-foreground">No student is averaging under 50%.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {attention.slice(0, 8).map((r) => (
                    <li key={r.uid}>
                      <Link to={`/analytics/students/${r.uid}`} className="flex items-center justify-between gap-3 py-2 text-sm hover:text-primary">
                        <span className="truncate text-foreground">{r.name}</span>
                        <span className="shrink-0 tabular-nums text-muted-foreground">
                          <span className="font-semibold text-destructive">{formatPercent(r.perf!.average)}</span> · {r.perf!.submissions} quizzes
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
              {noSubmissions > 0 && (
                <AnalyticsNote>
                  {noSubmissions} student{noSubmissions === 1 ? ' has' : 's have'} no quiz results in this period.
                </AnalyticsNote>
              )}
            </AnalyticsPanel>
          )}

          <AnalyticsPanel title="Students" description="Open a student for their full history">
            {loading ? (
              <TableSkeleton />
            ) : (
              <DataTable
                rows={studentRows}
                columns={studentColumns}
                rowKey={(r) => r.uid}
                initialSort={{ key: 'student', direction: 'asc' }}
                searchPlaceholder="Search students"
                searchText={(r) => `${r.name} ${r.email ?? ''}`}
                onRowClick={(r) => navigate(`/analytics/students/${r.uid}`)}
                empty={<AnalyticsEmptyState compact title="No students" description="Students appear here once they join the class." />}
              />
            )}
            <AnalyticsNote>Change compares each student's latest quiz with the one before it, in percentage points.</AnalyticsNote>
          </AnalyticsPanel>
        </>
      )}
    </section>
  );
}
